import { useGroupInvites, useMyGroup, useWithdrawInvite } from '@echo/core/react';
import { Button } from '@echo/ui-web';
import { Trans, useLingui } from '@lingui/react/macro';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { ToolbarBack } from '@/components/empty-state-frame';
import { GroupActionFailed, GroupLoadFailed, GroupLoading } from '@/components/group-load-state';
import { useGoBack } from '@/lib/use-go-back';

export const Route = createFileRoute('/_app/account_/groups_/$id_/invite_/$inviteId/withdraw')({
  component: WithdrawInvitePage,
});

/**
 * Members and Access › Pending invites › Withdraw invite: which invite and what withdrawing
 * means, then the destructive "Withdraw invite" back to Members and Access without it, or Cancel.
 * Expired invites can be withdrawn too. Core lists invites only to whoever may invite.
 */
function WithdrawInvitePage() {
  const { t } = useLingui();
  const { id, inviteId } = Route.useParams();
  const navigate = useNavigate();
  const toPeople = () =>
    void navigate({ to: '/account/groups/$id/people', params: { id }, replace: true });
  const goBack = useGoBack(toPeople);
  const { group, ...groups } = useMyGroup(id);
  const manages = group?.manages ?? false;
  const invites = useGroupInvites(id, manages);
  const withdrawing = useWithdrawInvite(id);
  const toolbar = (
    <ToolbarBack
      render={({ className, children }) => (
        <button type="button" className={className} onClick={goBack}>
          {children}
        </button>
      )}
    />
  );
  const heading = (
    <h1 className="text-display-sm font-bold">
      <Trans>Withdraw invite</Trans>
    </h1>
  );
  if (groups.isPending || (manages && invites.isPending)) {
    return (
      <>
        {toolbar}
        {heading}
        <GroupLoading />
      </>
    );
  }
  if (groups.isError || (manages && invites.isError)) {
    return (
      <>
        {toolbar}
        {heading}
        <div className="pt-content-md">
          <GroupLoadFailed
            onRetry={() => void (groups.isError ? groups.refetch() : invites.refetch())}
          />
        </div>
      </>
    );
  }
  const invite = invites.data?.find((candidate) => candidate.id === inviteId);
  // Withdrawn or answered (on its way out), or an unknown id from a link.
  if (!invite) return toolbar;

  const address = invite.address;
  const withdraw = () => {
    if (withdrawing.isPending) return;
    withdrawing.mutate(invite.id, { onSuccess: toPeople });
  };
  return (
    <>
      {toolbar}
      {heading}
      <div className="flex flex-col gap-content-md pt-content-md">
        <p className="text-body-lg text-on-surface-medium">
          {address ? (
            <Trans>Withdraw the invite to {address}? They won't be able to join with it.</Trans>
          ) : (
            <Trans>
              Withdraw this invite sent by text message? They won't be able to join with it.
            </Trans>
          )}
        </p>
        {withdrawing.isError ? <GroupActionFailed /> : null}
        <div className="flex flex-wrap justify-end gap-content-sm">
          <Button variant="secondary" onClick={goBack}>
            {t`Cancel`}
          </Button>
          <Button variant="destructive" loading={withdrawing.isPending} onClick={withdraw}>
            {t`Withdraw invite`}
          </Button>
        </div>
      </div>
    </>
  );
}
